<template>
    <div class="relative w-full" ref="dropdownContainer">
        <!-- Rotulo do campo -->
        <label :for="forLabel" class="block text-sm text-gray-600 mb-1">
            <span v-if="required" class="text-rose-600">*</span>
            {{ label }}
        </label>

        <!-- Campo de Busca -->
        <div class="relative">
            <input
                ref="searchInput"
                v-model="searchTerm"
                @input="handleInput"
                @focus="handleFocus"
                type="text"
                placeholder="Buscar contato..."
                class="defaultInput pr-10"
            />
            <!-- Botao Novo Contato -->
            <button
                type="button"
                @click="openCreateSlider"
                class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-orange-500 transition-colors"
                title="Adicionar novo contato"
            >
                <PlusCircleIcon class="w-5 h-5" />
            </button>
        </div>

        <!-- Dropdown com Resultados -->
        <ul
            v-if="showDropdown && contacts.length > 0"
            class="absolute w-full bg-white border rounded-md shadow-lg mt-1 max-h-60 overflow-auto z-50"
        >
            <li
                v-for="contact in contacts"
                :key="contact.id"
                @click="selectContact(contact)"
                :class="{'bg-blue-50 border-l-4 border-blue-500': selectedContact?.id === contact.id}"
                class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-100"
            >
                <!-- Avatar -->
                <img
                    v-if="contact.avatar"
                    :src="contact.avatar"
                    alt="Avatar"
                    class="w-8 h-8 rounded-full object-cover"
                />
                <div v-else class="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-sm font-medium">
                    {{ contact.name ? contact.name.charAt(0).toUpperCase() : '?' }}
                </div>

                <!-- Informacoes do Contato -->
                <div class="flex-1 overflow-hidden">
                    <p class="text-sm font-medium truncate">{{ contact.name }}</p>
                    <p class="text-xs text-gray-500 truncate">{{ contact.email || contact.phone || 'Sem email/telefone' }}</p>
                </div>
            </li>
        </ul>

        <!-- Mensagem de Nenhum Resultado -->
        <div
            v-else-if="showDropdown && contacts.length === 0 && searchTerm && !loading"
            class="absolute w-full bg-white border rounded-md shadow-lg mt-1 z-50"
        >
            <div class="px-3 py-2 text-gray-500 text-sm">
                Nenhum contato encontrado.
            </div>
            <button
                type="button"
                @click="openCreateSlider"
                class="w-full px-3 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2 border-t"
            >
                <PlusIcon class="w-4 h-4" />
                Criar novo contato "{{ searchTerm }}"
            </button>
        </div>

        <!-- Loading -->
        <div
            v-else-if="showDropdown && loading"
            class="absolute w-full bg-white border rounded-md shadow-lg mt-1 px-3 py-2 text-gray-500 z-50"
        >
            <div class="flex items-center gap-2">
                <svg class="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Buscando...
            </div>
        </div>

        <!-- Slider de Criar Contato -->
        <CreateContactSlider
            :open="showCreateSlider"
            :agentId="agent_id"
            :showAgentSelector="false"
            :initialName="searchTerm"
            @close="showCreateSlider = false"
            @created="handleContactCreated"
        />
    </div>
</template>


<script>
import { PlusCircleIcon, PlusIcon } from '@heroicons/vue/24/outline';
import CreateContactSlider from './CreateContactSlider.vue';

export default {
    name: 'ContactDropdown',
    components: {
        PlusCircleIcon,
        PlusIcon,
        CreateContactSlider,
    },
    props: {
        urlDefault: {
            type: String,
            default: 'crm/contacts'
        },
        label: {
            type: String,
            default: 'Contato'
        },
        required: {
            type: Boolean,
            default: false
        },
        agent_id: {
            type: String,
            default: null
        },
        filterByAgent: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            searchTerm: '',
            contacts: [],
            selectedContact: null,
            showDropdown: false,
            showCreateSlider: false,
            loading: false,
            debounceTimeout: null
        };
    },

    methods: {
        // Gera um ID unico para o campo
        forLabel() {
            return this.label.replace(/\s/g, '').toLowerCase();
        },

        // Busca contatos no backend
        async fetchContacts() {
            if (!this.searchTerm.trim()) {
                this.contacts = [];
                this.showDropdown = false;
                return;
            }

            this.loading = true;
            this.showDropdown = true;

            try {
                const query = {
                    search: this.searchTerm,
                    limit: 10,
                    attributes: 'id,avatar,name,email,phone',
                };

                // Só adiciona agent_id se fornecido E filterByAgent estiver ativo
                if (this.agent_id && this.filterByAgent) {
                    query.agent_id = this.agent_id;
                }

                const response = await this.$ApiService.get(`${this.urlDefault}`, query);

                if (response.error) {
                    console.error('Erro ao buscar contatos:', response.error);
                    this.contacts = [];
                    return;
                }

                this.contacts = response.data.rows || [];
            } catch (error) {
                console.error('Erro na requisicao de contatos:', error);
                this.contacts = [];
            } finally {
                this.loading = false;
            }
        },

        // Manipula o evento de digitacao com debounce
        handleInput() {
            clearTimeout(this.debounceTimeout);
            if (!this.searchTerm.trim()) {
                this.showDropdown = false;
                this.contacts = [];
                return;
            }

            this.debounceTimeout = setTimeout(() => {
                this.fetchContacts();
            }, 500);
        },

        // Manipula o foco no campo
        handleFocus() {
            if (this.searchTerm.trim()) {
                this.fetchContacts();
            }
        },

        // Seleciona um contato
        selectContact(contact) {
            this.searchTerm = contact.name;
            this.selectedContact = contact;
            this.showDropdown = false;
            this.contacts = [];
            this.$emit('contactSelected', {
                id: contact.id,
                name: contact.name,
                email: contact.email,
                phone: contact.phone,
                avatar: contact.avatar
            });
        },

        // Remove o contato selecionado
        removeSelectedContact() {
            this.selectedContact = null;
            this.searchTerm = '';
            this.$emit('contactSelected', null);

            // Foca novamente no campo de busca
            this.$nextTick(() => {
                this.$refs.searchInput.focus();
            });
        },

        // Abre o slider de criar contato
        openCreateSlider() {
            this.showDropdown = false;
            this.showCreateSlider = true;
        },

        // Quando um contato e criado
        handleContactCreated(contact) {
            // Seleciona automaticamente o contato criado
            this.selectContact(contact);
        },

        // Fecha dropdown ao clicar fora do componente
        handleClickOutside(event) {
            if (this.$refs.dropdownContainer && !this.$refs.dropdownContainer.contains(event.target)) {
                this.showDropdown = false;
            }
        }
    },

    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },

    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        clearTimeout(this.debounceTimeout);
    }
};

</script>

<style scoped>
ul {
    @apply text-sm;
}

li {
    @apply cursor-pointer;
}

img {
    @apply w-8 h-8 rounded-full object-cover;
}
</style>
